<template>
  <window-portal
    :open="dataProps.show"
    @close="handleClose"
  >
    <div
      class="dte-preview"
      :style="themeVariables"
    >
      <component :is="'style'">{{ activeStyles }}</component>

      <div class="dte-preview__toolbar">
        <div class="dte-preview__heading">
          <h1 class="dte-preview__title">
            Vista previa
          </h1>
          <p v-if="documentData" class="dte-preview__subtitle">
            {{ documentData.meta.invoiceTypeLabel }} - {{ documentData.meta.invoiceId }}
          </p>
        </div>

        <div v-if="showLayoutPicker" class="dte-preview__layout-picker">
          <span class="dte-preview__layout-label">Diseño</span>
          <button
            type="button"
            class="dte-button dte-button--toggle dte-button--ghost"
            :class="{ 'is-active': activeLayout === 'standard' }"
            :disabled="isBusy"
            @click="activeLayout = 'standard'"
          >
            Estandar
          </button>
          <button
            type="button"
            class="dte-button dte-button--toggle dte-button--ghost"
            :class="{ 'is-active': activeLayout === 'full' }"
            :disabled="isBusy"
            @click="activateFullLayout"
          >
            Completo
          </button>
          <button
            type="button"
            class="dte-button dte-button--toggle dte-button--ghost"
            :class="{ 'is-active': activeLayout === 'compact' }"
            :disabled="isBusy"
            @click="activeLayout = 'compact'"
          >
            Compacto
          </button>
        </div>

        <div class="dte-preview__controls">
          <button
            type="button"
            class="dte-button dte-button--accent"
            :disabled="isBusy"
            @click="printPdf"
          >
            Imprimir
          </button>
          <button
            type="button"
            class="dte-button dte-button--secondary"
            :disabled="isBusy"
            @click="downloadPdf"
          >
            Descargar PDF
          </button>
          <button
            v-if="!hideQr"
            type="button"
            class="dte-button dte-button--accent"
            :disabled="isBusy"
            @click="sendMail"
          >
            Enviar correo
          </button>
          <button
            v-if="!hideQr"
            type="button"
            class="dte-button dte-button--ghost"
            :disabled="isBusy"
            @click="downloadJson"
          >
            Descargar JSON
          </button>
        </div>
      </div>

      <div v-if="mailLabel" class="dte-preview__status">
        {{ mailLabel }}
      </div>

      <div class="dte-preview__canvas">
        <div v-if="documentData" class="dte-preview__pages">
          <print-pos-dte-letter-standard-layout
            v-if="activeLayout === 'standard'"
            ref="layoutRenderer"
            :document-data="documentData"
            :pages="standardPages"
          />

          <print-pos-dte-letter-full-layout
            v-else-if="activeLayout === 'full'"
            ref="layoutRenderer"
            :document-data="documentData"
            :pages="fullPages"
          />

          <print-pos-dte-letter-compact-layout
            v-else
            ref="layoutRenderer"
            :document-data="documentData"
            :pages="compactPages"
          />
        </div>

        <div v-if="isBusy" class="dte-preview__loading">
          <div class="dte-preview__spinner"></div>
          <div class="dte-preview__loading-label">
            {{ busyLabel || "Procesando..." }}
          </div>
        </div>
      </div>

      <div
        v-if="layoutMeasurement && measurementRendererComponent"
        ref="layoutMeasurementHost"
        style="position: fixed; left: -20000px; top: 0; width: 8.5in; visibility: hidden; pointer-events: none; z-index: -1;"
      >
        <component
          :is="measurementRendererComponent"
          ref="measurementRenderer"
          :key="layoutMeasurement.renderKey"
          :document-data="layoutMeasurement.documentData"
          :pages="layoutMeasurement.pages"
        />
      </div>
    </div>
  </window-portal>
</template>

<script>
import Vue from "vue";
import QRCode from "qrcode";

import WindowPortal from "../WindowPortal.vue";
import GuaranteePrintBlock from "@/components/ReportsAndPrintings/GuaranteePrintBlock.vue";
import PrintPosDteLetterCompactLayout from "./layouts/PrintPosDteLetterCompactLayout.vue";
import PrintPosDteLetterFullLayout from "./layouts/PrintPosDteLetterFullLayout.vue";
import PrintPosDteLetterStandardLayout from "./layouts/PrintPosDteLetterStandardLayout.vue";
import commonStyles from "./styles/commonStyles";
import compactLayoutStyles from "./styles/compactLayoutStyles";
import fullLayoutStyles from "./styles/fullLayoutStyles";
import standardLayoutStyles from "./styles/standardLayoutStyles";
import {
  buildPreviewPdfArrayBuffer,
} from "./utils/dtePrintExporter";
import {
  assignCuerpoDocumento,
  normalizeDtePrintData,
} from "./utils/dtePrintNormalizer";
import { paginateDteDocument } from "./utils/dtePrintPagination";
import { htmlToText } from "./utils/dtePrintFormatters";
import { lineBreaker } from "@/helpers/textformatting";
import {
  DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY,
  getBranchConfiguredDteTemplateLayoutKey,
  getDefaultDteTemplateSettings,
  isDteTemplateHideAdvertisingOnPdfDownload,
  hasBranchConfiguredDteTemplateSelection,
  isDteTemplateHideAdvertisingOnPrint,
  isBranchDteTemplateV2Enabled,
  mapDteRenderLayoutToTemplateKey,
  mapDteTemplateLayoutToRenderKey,
  mapDteTemplateSettingsToTheme,
  safeResolveDteTemplateSettings,
} from "@/helpers/dteTemplateSettings";

export default {
  name: "PrintPosDteLetter",
  components: {
    WindowPortal,
    PrintPosDteLetterCompactLayout,
    PrintPosDteLetterFullLayout,
    PrintPosDteLetterStandardLayout,
    GuaranteePrintBlock,
  },
  props: {
    dataProps: {
      type: Object,
      default: () => ({}),
    },
    hideQr: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const branch =
      this.dataProps &&
      this.dataProps.data &&
      this.dataProps.data.branch
        ? this.dataProps.data.branch
        : {};
    const initialTemplateLayoutKey =
      getBranchConfiguredDteTemplateLayoutKey(branch) ||
      DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY;

    return {
      activeLayout: mapDteTemplateLayoutToRenderKey(initialTemplateLayoutKey),
      compactPages: [],
      fullPages: [],
      standardPages: [],
      documentData: null,
      loading: false,
      previewLoading: false,
      busyLabel: "",
      mailLabel: "",
      templateSettingsByLayout: {},
      layoutMeasurement: null,
      measurementToken: 0,
      measurementRenderSequence: 0,
      refreshToken: 0,
      suppressActiveLayoutRefresh: false,
    };
  },
  computed: {
    templateFeatureEnabled() {
      const branch =
        this.dataProps &&
        this.dataProps.data &&
        this.dataProps.data.branch
          ? this.dataProps.data.branch
          : {};
      return isBranchDteTemplateV2Enabled(branch, 0);
    },
    configuredTemplateLayoutKey() {
      const branch =
        this.dataProps &&
        this.dataProps.data &&
        this.dataProps.data.branch
          ? this.dataProps.data.branch
          : {};
      return getBranchConfiguredDteTemplateLayoutKey(branch);
    },
    templateV2Enabled() {
      const branch =
        this.dataProps &&
        this.dataProps.data &&
        this.dataProps.data.branch
          ? this.dataProps.data.branch
          : {};
      return hasBranchConfiguredDteTemplateSelection(branch);
    },
    selectedTemplateLayoutKey() {
      return (
        this.configuredTemplateLayoutKey ||
        DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY
      );
    },
    showLayoutPicker() {
      return (
        this.templateV2Enabled &&
        String(process.env.VUE_APP_ENV || "").trim() === "1"
      );
    },
    isBusy() {
      return this.loading || this.previewLoading;
    },
    measurementRendererComponent() {
      if (!this.layoutMeasurement) {
        return null;
      }

      if (this.layoutMeasurement.layout === "standard") {
        return this.$options.components.PrintPosDteLetterStandardLayout;
      }

      if (this.layoutMeasurement.layout === "compact") {
        return this.$options.components.PrintPosDteLetterCompactLayout;
      }

      if (this.layoutMeasurement.layout === "full") {
        return this.$options.components.PrintPosDteLetterFullLayout;
      }

      return null;
    },
    activeStyles() {
      return [
        commonStyles,
        fullLayoutStyles,
        compactLayoutStyles,
        standardLayoutStyles,
      ].join("\n");
    },
    themeVariables() {
      const theme = (this.documentData && this.documentData.theme) || {};
      return {
        "--dte-brand": theme.brand,
        "--dte-brand-contrast": theme.brandContrast,
        "--dte-accent": theme.accent,
        "--dte-surface": theme.surface,
        "--dte-border": theme.border,
        "--dte-table-border": theme.tableBorderColor,
        "--dte-card-radius": theme.cardBorderRadiusPx
          ? `${theme.cardBorderRadiusPx}px`
          : "14px",
        "--dte-table-radius": theme.tableBorderRadiusPx
          ? `${theme.tableBorderRadiusPx}px`
          : "0px",
        "--dte-text": theme.text,
        "--dte-muted": theme.muted,
        "--dte-card-header-gradient-strong": theme.cardHeaderGradientStrong,
        "--dte-card-header-gradient-soft": theme.cardHeaderGradientSoft,
        "--dte-title-font-size": theme.titleFontSizePx
          ? `${theme.titleFontSizePx}px`
          : "18px",
        "--dte-text-font-size": theme.textFontSizePx
          ? `${theme.textFontSizePx}px`
          : "12px",
        "--dte-detail-font-size": theme.detailFontSizePx
          ? `${theme.detailFontSizePx}px`
          : "11px",
      };
    },
  },
  watch: {
    "dataProps.show"(value) {
      if (value) {
        this.templateSettingsByLayout = {};
        this.refreshDocument({ resetLayout: true });
      }
    },
    dataProps: {
      deep: true,
      handler(value) {
        if (value && value.show) {
          this.templateSettingsByLayout = {};
          this.refreshDocument({ resetLayout: true });
        }
      },
    },
    activeLayout() {
      if (this.suppressActiveLayoutRefresh) {
        return;
      }

      if (this.dataProps && this.dataProps.show) {
        this.refreshDocument();
      }
    },
  },
  mounted() {
    if (this.dataProps.show) {
      this.refreshDocument({ resetLayout: true });
    }
  },
  methods: {
    getBranchId() {
      return (
        this.dataProps &&
        this.dataProps.data &&
        this.dataProps.data.branch &&
        this.dataProps.data.branch.ID_Branch
      );
    },
    async loadTemplateSettings(templateLayoutKey) {
      if (this.templateSettingsByLayout[templateLayoutKey]) {
        return this.templateSettingsByLayout[templateLayoutKey];
      }

      const branchId = this.getBranchId();
      if (!branchId) {
        return getDefaultDteTemplateSettings(templateLayoutKey);
      }

      try {
        const response = await this.$API.branchDteTemplateSettings.getSettings(
          branchId,
          templateLayoutKey
        );
        const settings = safeResolveDteTemplateSettings(
          response && response.data ? response.data.settings : {},
          templateLayoutKey
        );
        this.$set(this.templateSettingsByLayout, templateLayoutKey, settings);
        return settings;
      } catch (error) {
        return getDefaultDteTemplateSettings(templateLayoutKey);
      }
    },
    async refreshDocument(options = {}) {
      const requestToken = ++this.refreshToken;

      if (options.resetLayout) {
        const initialLayout = this.getInitialRenderLayout();
        if (this.activeLayout !== initialLayout) {
          this.suppressActiveLayoutRefresh = true;
          this.activeLayout = initialLayout;
          this.suppressActiveLayoutRefresh = false;
        }
      }

      this.previewLoading = true;
      if (!this.loading) {
        this.busyLabel = "Cargando vista previa...";
      }

      try {
        const state = await this.buildRenderState({
          activeLayout: this.activeLayout,
          forPrint: options.forPrint !== false,
        });

        if (requestToken !== this.refreshToken) {
          return;
        }

        this.applyRenderState(state);
      } finally {
        if (requestToken === this.refreshToken) {
          this.previewLoading = false;
          if (!this.loading) {
            this.busyLabel = "";
          }
        }
      }
    },
    async buildRenderState(options = {}) {
      const targetLayout = options.activeLayout || this.activeLayout;
      const templateLayoutKey = this.configuredTemplateLayoutKey
        ? mapDteRenderLayoutToTemplateKey(targetLayout)
        : DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY;
      const templateSettings = await this.loadTemplateSettings(templateLayoutKey);
      const resolvedTheme = mapDteTemplateSettingsToTheme(
        templateSettings,
        this.theme,
        templateLayoutKey
      );

      const normalized = normalizeDtePrintData(this.dataProps, {
        hideQr: this.hideQr,
        theme: resolvedTheme,
        templateSettings,
        templateLayoutKey,
        forPrint: Boolean(options.forPrint),
      });

      const guaranteeHtml = await this.loadGuaranteeHtml(normalized);
      normalized.guaranteeHtml = guaranteeHtml;

      if (normalized.qrUrl && !this.hideQr) {
        normalized.qrImage = await QRCode.toDataURL(normalized.qrUrl, {
          errorCorrectionLevel: "H",
          type: "image/png",
          quality: 0.92,
          width: 120,
          margin: 1,
        });
      }

      const activePages = await this.measureLayoutPages(normalized, targetLayout);
      const showGuaranteeInline = await this.shouldRenderGuaranteeInline(
        activePages,
        guaranteeHtml,
        targetLayout,
        normalized
      );

      normalized.showGuaranteeInline = showGuaranteeInline;

      return {
        documentData: normalized,
        fullPages: targetLayout === "full" ? activePages : [],
        compactPages: targetLayout === "compact" ? activePages : [],
        standardPages: targetLayout === "standard" ? activePages : [],
      };
    },
    waitForFrame() {
      return new Promise((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
    },
    getMeasurementDocumentData(documentData) {
      return {
        ...documentData,
        showFullPageBanner: false,
      };
    },
    buildMeasuredRenderPage(pageIndex, items, documentData, showTail = false) {
      const isFirstPage = pageIndex === 0;

      return {
        number: pageIndex + 1,
        isFirst: isFirstPage,
        isLast: Boolean(showTail),
        showReceiver: isFirstPage,
        showRelated:
          isFirstPage && (documentData.relatedDocuments || []).length > 0,
        showAssociated:
          isFirstPage && (documentData.associatedDocuments || []).length > 0,
        showThirdPartySale:
          isFirstPage && Boolean(documentData.thirdPartySale),
        showTail: Boolean(showTail),
        items,
      };
    },
    getRenderedFitSafetyPx(layout) {
      if (layout === "standard") {
        return 24;
      }

      if (layout === "compact") {
        return 22;
      }

      return 20;
    },
    isRenderedSheetWithinBounds(sheetElement, layout) {
      const pageNumber = sheetElement.querySelector(".dte-sheet__page-number");

      if (!pageNumber) {
        return false;
      }

      const sheetTop = sheetElement.getBoundingClientRect().top;
      const contentBottom = this.getSheetContentBottom(sheetElement, sheetTop);
      const pageLimit =
        pageNumber.getBoundingClientRect().top -
        sheetTop -
        this.getRenderedFitSafetyPx(layout);

      return contentBottom <= pageLimit;
    },
    async renderMeasurementCandidatePages(
      documentData,
      layout,
      pages,
      measurementToken
    ) {
      this.layoutMeasurement = {
        token: measurementToken,
        renderKey: `${measurementToken}-${++this.measurementRenderSequence}`,
        layout,
        documentData: this.getMeasurementDocumentData(documentData),
        pages,
      };

      await this.$nextTick();
      await this.waitForFrame();

      if (
        !this.layoutMeasurement ||
        this.layoutMeasurement.token !== measurementToken
      ) {
        return [];
      }

      const renderer = this.$refs.measurementRenderer;
      if (!renderer || typeof renderer.getPageElements !== "function") {
        return [];
      }

      if (renderer.$el) {
        await this.waitForPreviewPdfImages(renderer.$el);
      }

      await this.waitForFrame();

      if (
        !this.layoutMeasurement ||
        this.layoutMeasurement.token !== measurementToken
      ) {
        return [];
      }

      return renderer.getPageElements().slice(0, pages.length);
    },
    async doesRenderedPageFit(
      documentData,
      layout,
      pageIndex,
      items,
      showTail,
      measurementToken
    ) {
      const pages = [
        this.buildMeasuredRenderPage(
          pageIndex,
          items,
          documentData,
          showTail
        ),
      ];
      const sheets = await this.renderMeasurementCandidatePages(
        documentData,
        layout,
        pages,
        measurementToken
      );

      if (!sheets.length) {
        return false;
      }

      return this.isRenderedSheetWithinBounds(sheets[0], layout);
    },
    async findMaxRenderedItemsForPage(
      documentData,
      layout,
      pageIndex,
      pendingItems,
      showTail,
      measurementToken
    ) {
      let low = showTail ? 0 : 1;
      let high = pendingItems.length;
      let best = 0;

      while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        const candidateItems = pendingItems.slice(0, middle);
        const fits = await this.doesRenderedPageFit(
          documentData,
          layout,
          pageIndex,
          candidateItems,
          showTail,
          measurementToken
        );

        if (fits) {
          best = middle;
          low = middle + 1;
        } else {
          high = middle - 1;
        }
      }

      return best;
    },
    buildFinalMeasuredPages(pages, documentData) {
      return pages.map((page, index) => ({
        ...page,
        number: index + 1,
        isFirst: index === 0,
        isLast: index === pages.length - 1,
        showReceiver: index === 0,
        showRelated:
          index === 0 && (documentData.relatedDocuments || []).length > 0,
        showAssociated:
          index === 0 && (documentData.associatedDocuments || []).length > 0,
        showThirdPartySale:
          index === 0 && Boolean(documentData.thirdPartySale),
        showTail: index === pages.length - 1,
      }));
    },
    getSheetContentBottom(sheetElement, sheetTop) {
      return Array.from(sheetElement.children || [])
        .filter((element) => {
          if (!element || !element.classList) {
            return false;
          }

          return !element.classList.contains("dte-sheet__page-number");
        })
        .reduce((maxBottom, element) => {
          return Math.max(
            maxBottom,
            element.getBoundingClientRect().bottom - sheetTop
          );
        }, 0);
    },
    async measureLayoutPages(documentData, layout) {
      this.layoutMeasurement = null;
      await this.$nextTick();
      return paginateDteDocument(documentData, layout);
    },
    activateFullLayout() {
      this.activeLayout = "full";
    },
    applyRenderState(state) {
      this.documentData = state.documentData;
      this.fullPages = state.fullPages;
      this.compactPages = state.compactPages;
      this.standardPages = state.standardPages;
    },
    captureRenderState() {
      return {
        documentData: this.documentData,
        fullPages: this.fullPages,
        compactPages: this.compactPages,
        standardPages: this.standardPages,
      };
    },
    async runWithLoading(label, callback) {
      this.loading = true;
      this.busyLabel = label;

      try {
        return await callback();
      } finally {
        this.loading = false;
        if (!this.previewLoading) {
          this.busyLabel = "";
        }
      }
    },
    shouldHideAdvertisingOnOutput(mode = "print") {
      const templateLayoutKey = this.configuredTemplateLayoutKey
        ? mapDteRenderLayoutToTemplateKey(this.activeLayout)
        : DEFAULT_BRANCH_DTE_TEMPLATE_LAYOUT_KEY;
      const settings =
        (this.documentData && this.documentData.templateSettings) ||
        this.templateSettingsByLayout[templateLayoutKey] ||
        getDefaultDteTemplateSettings(templateLayoutKey);
      if (mode === "downloadPdf") {
        return isDteTemplateHideAdvertisingOnPdfDownload(
          settings,
          templateLayoutKey
        );
      }
      return isDteTemplateHideAdvertisingOnPrint(settings, templateLayoutKey);
    },
    async withOutputRenderState(callback, mode = "print") {
      if (!this.shouldHideAdvertisingOnOutput(mode)) {
        return callback();
      }

      const previousState = this.captureRenderState();
      const printState = await this.buildRenderState({
        activeLayout: this.activeLayout,
        forPrint: true,
      });

      this.applyRenderState(printState);
      await this.$nextTick();

      try {
        return await callback();
      } finally {
        this.applyRenderState(previousState);
        await this.$nextTick();
      }
    },
    getInitialRenderLayout() {
      return mapDteTemplateLayoutToRenderKey(this.selectedTemplateLayoutKey);
    },
    getGuaranteeBusinessId(documentData) {
      const rawBranch = (documentData && documentData.rawData && documentData.rawData.branch) || {};
      return (
        rawBranch.ID_Business ||
        rawBranch.businessId ||
        JSON.parse(localStorage.getItem("user"))?.businessid ||
        null
      );
    },
    getGuaranteeBranchId(documentData) {
      const rawBranch = (documentData && documentData.rawData && documentData.rawData.branch) || {};
      return (
        rawBranch.ID_Branch ||
        JSON.parse(localStorage.getItem("branch"))?.ID_Branch ||
        null
      );
    },
    async loadGuaranteeHtml(documentData) {
      const businessId = this.getGuaranteeBusinessId(documentData);
      const branchId = this.getGuaranteeBranchId(documentData);

      if (!businessId) {
        return "";
      }

      try {
        const response = await this.$API.businessGuarantees.getEffective({
          ID_Business: businessId,
          ID_Branch: branchId,
          GuaranteeType: "COMPROBANTE",
        });

        return (response && response.data && response.data.ContentHtml) || "";
      } catch (error) {
        return "";
      }
    },
    estimateGuaranteeUnits(html, layout) {
      const text = htmlToText(html || "");
      if (!text) {
        return 0;
      }

      const lines = lineBreaker(text)?.[1] || 1;
      const layoutFactor = layout === "compact" ? 0.58 : layout === "standard" ? 0.62 : 0.66;
      return 1.4 + (lines * layoutFactor);
    },
    async measureGuaranteeBlockHeight(documentData) {
      if (!documentData || !documentData.guaranteeHtml) {
        return 0;
      }

      const ownerDocument = (this.$el && this.$el.ownerDocument) || document;
      const host = ownerDocument.createElement("div");
      let instance = null;
      host.style.position = "fixed";
      host.style.left = "-20000px";
      host.style.top = "0";
      host.style.width = "8.5in";
      host.style.visibility = "hidden";
      host.style.pointerEvents = "none";
      host.style.zIndex = "-1";
      ownerDocument.body.appendChild(host);

      try {
        const GuaranteeCtor = Vue.extend(GuaranteePrintBlock);
        instance = new GuaranteeCtor({
          propsData: {
            html: documentData.guaranteeHtml,
            businessId: this.getGuaranteeBusinessId(documentData),
            branchId: this.getGuaranteeBranchId(documentData),
            guaranteeType: "COMPROBANTE",
            fontSize: "11px",
          },
        });

        instance.$mount();
        host.appendChild(instance.$el);

        await this.$nextTick();
        await this.waitForFrame();

        return instance.$el.getBoundingClientRect().height || 0;
      } catch (error) {
        return 0;
      } finally {
        if (instance && typeof instance.$destroy === "function") {
          instance.$destroy();
        }
        host.remove();
      }
    },
    async shouldRenderGuaranteeInline(activePages, html, layout, documentData) {
      if (!html || !activePages.length) {
        return false;
      }

      const sheets = await this.renderMeasurementCandidatePages(
        documentData,
        layout,
        activePages,
        `guarantee-${Date.now()}`
      );

      const lastSheet = sheets[sheets.length - 1];
      if (!lastSheet) {
        return false;
      }

      const pageNumber = lastSheet.querySelector(".dte-sheet__page-number");
      if (!pageNumber) {
        return false;
      }

      const sheetTop = lastSheet.getBoundingClientRect().top;
      const contentBottom = this.getSheetContentBottom(lastSheet, sheetTop);
      const pageLimit =
        pageNumber.getBoundingClientRect().top -
        sheetTop -
        this.getRenderedFitSafetyPx(layout);
      const availablePx = Math.max(0, pageLimit - contentBottom);
      const guaranteeHeight = await this.measureGuaranteeBlockHeight(documentData);

      return guaranteeHeight > 0 && guaranteeHeight <= availablePx;
    },
    handleClose() {
      this.dataProps.show = false;
      if (typeof this.dataProps.closeCallback === "function") {
        this.dataProps.closeCallback();
      }
    },
    getRenderedPages() {
      const renderer = this.$refs.layoutRenderer;
      if (!renderer || typeof renderer.getPageElements !== "function") {
        return [];
      }

      return renderer.getPageElements();
    },
    async ensureRenderedDocument() {
      if (!this.documentData) {
        await this.refreshDocument();
      }

      await this.$nextTick();
      return this.getRenderedPages();
    },
    async inlineImagesForPreviewPdf(rootElement) {
      const images = Array.from(rootElement.querySelectorAll("img"));

      await Promise.all(images.map(async (image) => {
        const source = image.getAttribute("src");

        if (!source || source.startsWith("data:")) {
          return;
        }

        const resolved = await this.resolveAssetToDataUrl(source);
        if (resolved) {
          image.setAttribute("src", resolved);
        }
      }));
    },
    async waitForPreviewPdfImages(rootElement) {
      const images = Array.from(rootElement.querySelectorAll("img"));

      await Promise.all(images.map((image) => new Promise((resolve) => {
        if (image.complete) {
          resolve();
          return;
        }

        image.onload = () => resolve();
        image.onerror = () => resolve();
      })));
    },
    async buildPreviewPdfPageElements() {
      const pages = await this.ensureRenderedDocument();
      const ownerDocument = (pages[0] && pages[0].ownerDocument) || this.$el.ownerDocument || document;
      const container = ownerDocument.createElement("div");
      const themeVariables = this.themeVariables || {};

      container.style.position = "fixed";
      container.style.left = "-20000px";
      container.style.top = "0";
      container.style.width = "8.5in";
      container.style.background = "#ffffff";
      container.style.color = "var(--dte-text, #1b2a3a)";
      container.style.fontFamily = "\"Trebuchet MS\", \"Segoe UI\", sans-serif";
      container.style.zIndex = "-1";

      Object.entries(themeVariables).forEach(([key, value]) => {
        if (value) {
          container.style.setProperty(key, value);
        }
      });

      const clones = pages.map((page) => {
        const clone = page.cloneNode(true);
        Object.entries(themeVariables).forEach(([key, value]) => {
          if (value) {
            clone.style.setProperty(key, value);
          }
        });
        container.appendChild(clone);
        return clone;
      });

      ownerDocument.body.appendChild(container);

      try {
        await this.inlineImagesForPreviewPdf(container);
        await this.waitForPreviewPdfImages(container);
        return { container, elements: clones, ownerDocument };
      } catch (error) {
        container.remove();
        throw error;
      }
    },
    async resolveAssetToDataUrl(source) {
      if (!source || source.startsWith("data:")) {
        return source || "";
      }

      try {
        const response = await this.$API.utilities.getImageInBase64({ file: source });
        if (response && response.imageBase64) {
          return response.imageBase64;
        }
      } catch (error) {
        // fallback below
      }

      try {
        const fetchResponse = await fetch(source);
        const blob = await fetchResponse.blob();
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        return "";
      }
    },
    async buildCurrentPreviewPdfArrayBuffer() {
      let previewRender = null;

      try {
        previewRender = await this.buildPreviewPdfPageElements();
        return await buildPreviewPdfArrayBuffer(previewRender.elements);
      } finally {
        if (previewRender && previewRender.container) {
          previewRender.container.remove();
        }
      }
    },
    getPagesByActiveLayout() {
      if (this.activeLayout === "full") {
        return this.fullPages;
      }

      if (this.activeLayout === "standard") {
        return this.standardPages;
      }

      return this.compactPages;
    },
    async printDocument() {
      await this.withOutputRenderState(async () => {
        await this.ensureRenderedDocument();
        const currentWindow = this.$el.ownerDocument.defaultView || window;
        currentWindow.focus();
        currentWindow.print();
      }, "print");
    },
    async printPdf() {
      this.mailLabel = "";
      let previewRender = null;

      await this.runWithLoading("Generando PDF para impresion...", async () => {
        try {
          const pdf = await this.withOutputRenderState(async () => {
            previewRender = await this.buildPreviewPdfPageElements();
            return buildPreviewPdfArrayBuffer(previewRender.elements);
          }, "print");
          const blob = new Blob([pdf], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const ownerDocument = previewRender.ownerDocument || this.$el.ownerDocument || document;
          const iframe = ownerDocument.createElement("iframe");

          iframe.style.position = "fixed";
          iframe.style.width = "0";
          iframe.style.height = "0";
          iframe.style.border = "0";
          iframe.style.right = "0";
          iframe.style.bottom = "0";
          iframe.src = url;

          iframe.onload = () => {
            setTimeout(() => {
              try {
                if (iframe.contentWindow) {
                  iframe.contentWindow.focus();
                  iframe.contentWindow.print();
                }
              } finally {
                setTimeout(() => {
                  URL.revokeObjectURL(url);
                  if (iframe.parentNode) {
                    iframe.parentNode.removeChild(iframe);
                  }
                }, 1500);
              }
            }, 350);
          };

          ownerDocument.body.appendChild(iframe);
        } finally {
          if (previewRender && previewRender.container) {
            previewRender.container.remove();
          }
        }
      });
    },
    async downloadPdf() {
      this.mailLabel = "";

      await this.runWithLoading("Generando PDF...", async () => {
        const pdf = await this.withOutputRenderState(
          () => this.buildCurrentPreviewPdfArrayBuffer(),
          "downloadPdf"
        );
        const blob = new Blob([pdf], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.documentData.fileBaseName}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      });
    },
    async sendMail() {
      this.mailLabel = "Enviando correo...";

      await this.runWithLoading("Generando documento para correo...", async () => {
        try {
          const pdfArrayBuffer = await this.withOutputRenderState(() =>
            this.buildCurrentPreviewPdfArrayBuffer()
          );
          const rawData = this.documentData.rawData;

          assignCuerpoDocumento(rawData);

          const pdfBuffer = typeof Buffer !== "undefined"
            ? Buffer.from(pdfArrayBuffer)
            : new Uint8Array(pdfArrayBuffer);

          await this.$API.invoices.sendInvoiceMail({
            dtefilebuffer: pdfBuffer,
            dtejson: {
              ...(rawData.header?.jsonMH || {}),
              ID_Invoice: rawData.dteJson?.ID_Invoice || rawData.header?.ID_Invoice,
            },
            dtefilebufferfilename: this.documentData.fileBaseName,
          });

          this.mailLabel = "Se envio el correo correctamente";
          this.$emit("mailSent");
        } catch (error) {
          this.mailLabel = `Ocurrio un error al enviar el correo: ${error}`;
        }
      });
    },
    downloadJson() {
      if (!this.documentData) {
        return;
      }

      const blob = new Blob(
        [JSON.stringify(this.documentData.jsonPayload, null, 2)],
        { type: "application/json" }
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${this.documentData.fileBaseName}.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
